/**
 * 만료일 동기화 서비스
 * 그룹 내 멤버들의 만료일을 자동으로 동기화
 */

import { ref, get, onValue, off } from 'firebase/database';
import { database, DB_PATHS } from '../config/firebase';
import { accountSyncService } from './accountSyncService';
import { batchUpdate } from '../utils/accountSyncTransaction';
import { ACCOUNT_SYNC_PATHS, DEFAULT_GROUP_SETTINGS } from '../config/accountSyncConfig';
import { AccountSyncAction } from '../types/accountSync';

class ExpireDateSyncService {
  private listeners: Map<string, any> = new Map();
  private previousExpireDates: Map<string, string> = new Map();

  /**
   * 만료일 변경 감지 및 동기화
   */
  async handleExpireDateChange(
    phoneNumber: string,
    newExpireDate: string,
    oldExpireDate: string,
    executedBy: string
  ): Promise<void> {
    try {
      // 1. 해당 기사가 속한 그룹 확인
      const group = await accountSyncService.getAccountGroup(phoneNumber);
      if (!group) {
        console.log('그룹에 속하지 않은 기사, 동기화 불필요');
        return;
      }

      // 자동 동기화 설정 확인 (향후 설정 기능 추가 시 사용)
      if (!DEFAULT_GROUP_SETTINGS.AUTO_SYNC_EXPIRE_DATE) {
        console.log('자동 동기화 비활성화됨');
        return;
      }

      // 2. 그룹 멤버 조회
      const members = group.members;
      
      // 3. 트랜잭션으로 모든 멤버 만료일 업데이트
      const updates: { [key: string]: any } = {};
      
      for (const member of members) {
        // 자기 자신도 포함하여 모든 멤버 업데이트
        updates[`${DB_PATHS.DRIVERS}/${member}/expiredate`] = newExpireDate;
      }
      
      // 4. 이력 기록
      const historyId = this.generateHistoryId();
      updates[`${ACCOUNT_SYNC_PATHS.SYNC_HISTORY}/${historyId}`] = {
        id: historyId,
        groupId: group.groupId,
        action: 'EXPIRE_SYNC' as AccountSyncAction,
        phoneNumber: phoneNumber,
        executedBy: executedBy,
        executedAt: new Date().toISOString(),
        oldValue: oldExpireDate,
        newValue: newExpireDate,
        reason: `그룹 만료일 동기화 - ${phoneNumber} 변경에 따른 동기화 (멤버: ${members.length}명)`,
      };
      
      // 5. 배치 업데이트 실행
      await batchUpdate(updates);
      
      console.log(`그룹 ${group.groupId}의 만료일이 ${newExpireDate}로 동기화되었습니다.`);
    } catch (error) {
      console.error('만료일 동기화 실패:', error);
      throw error;
    }
  }

  /**
   * Firebase 리스너를 통한 자동 감지 설정
   */
  setupExpireDateListener(phoneNumber: string, executedBy: string): void {
    // 이미 리스너가 있으면 제거
    if (this.listeners.has(phoneNumber)) {
      this.removeExpireDateListener(phoneNumber);
    }

    const driverRef = ref(database, `${DB_PATHS.DRIVERS}/${phoneNumber}/expiredate`);
    
    // 초기값 저장
    get(driverRef).then(snapshot => {
      if (snapshot.exists()) {
        this.previousExpireDates.set(phoneNumber, snapshot.val());
      }
    });
    
    // 리스너 설정
    const listener = onValue(driverRef, async (snapshot) => {
      const newExpireDate = snapshot.val();
      const previousExpireDate = this.previousExpireDates.get(phoneNumber);
      
      if (newExpireDate && previousExpireDate && newExpireDate !== previousExpireDate) {
        // 만료일이 변경되었을 때 자동 동기화
        console.log(`만료일 변경 감지: ${phoneNumber} - ${previousExpireDate} → ${newExpireDate}`);
        
        // 무한 루프 방지: 동기화로 인한 변경인지 확인
        const group = await accountSyncService.getAccountGroup(phoneNumber);
        if (group) {
          // 다른 멤버들의 만료일 확인
          let needSync = false;
          for (const member of group.members) {
            if (member !== phoneNumber) {
              const memberRef = ref(database, `${DB_PATHS.DRIVERS}/${member}/expiredate`);
              const memberSnapshot = await get(memberRef);
              if (memberSnapshot.exists() && memberSnapshot.val() !== newExpireDate) {
                needSync = true;
                break;
              }
            }
          }
          
          if (needSync) {
            await this.handleExpireDateChange(
              phoneNumber,
              newExpireDate,
              previousExpireDate,
              executedBy
            );
          }
        }
        
        // 새 값을 이전 값으로 업데이트
        this.previousExpireDates.set(phoneNumber, newExpireDate);
      }
    });
    
    this.listeners.set(phoneNumber, listener);
  }

  /**
   * 리스너 제거
   */
  removeExpireDateListener(phoneNumber: string): void {
    const listener = this.listeners.get(phoneNumber);
    if (listener) {
      const driverRef = ref(database, `${DB_PATHS.DRIVERS}/${phoneNumber}/expiredate`);
      off(driverRef, 'value', listener);
      this.listeners.delete(phoneNumber);
      this.previousExpireDates.delete(phoneNumber);
    }
  }

  /**
   * 모든 리스너 제거
   */
  removeAllListeners(): void {
    for (const phoneNumber of this.listeners.keys()) {
      this.removeExpireDateListener(phoneNumber);
    }
  }

  /**
   * 그룹의 모든 멤버에 대한 리스너 설정
   */
  async setupGroupListeners(groupId: string, executedBy: string): Promise<void> {
    const groupRef = ref(database, `${ACCOUNT_SYNC_PATHS.ACCOUNT_GROUPS}/${groupId}`);
    const snapshot = await get(groupRef);
    
    if (snapshot.exists()) {
      const group = snapshot.val();
      for (const member of group.members) {
        this.setupExpireDateListener(member, executedBy);
      }
    }
  }

  /**
   * 수동 동기화 실행
   * UI에서 "동기화" 버튼을 누를 때 사용
   */
  async syncGroupExpireDates(phoneNumber: string, executedBy: string): Promise<void> {
    const group = await accountSyncService.getAccountGroup(phoneNumber);
    if (!group) {
      throw new Error('그룹을 찾을 수 없습니다.');
    }

    // 주 계정의 만료일을 기준으로 동기화
    const primaryDriverRef = ref(database, `${DB_PATHS.DRIVERS}/${group.primaryPhone}/expiredate`);
    const primarySnapshot = await get(primaryDriverRef);
    
    if (!primarySnapshot.exists()) {
      throw new Error('주 계정의 만료일을 찾을 수 없습니다.');
    }

    const primaryExpireDate = primarySnapshot.val();
    const updates: { [key: string]: any } = {};

    // 모든 멤버의 만료일을 주 계정과 동일하게 설정
    for (const member of group.members) {
      if (member !== group.primaryPhone) {
        updates[`${DB_PATHS.DRIVERS}/${member}/expiredate`] = primaryExpireDate;
      }
    }

    if (Object.keys(updates).length > 0) {
      // 이력 기록
      const historyId = this.generateHistoryId();
      updates[`${ACCOUNT_SYNC_PATHS.SYNC_HISTORY}/${historyId}`] = {
        id: historyId,
        groupId: group.groupId,
        action: 'EXPIRE_SYNC' as AccountSyncAction,
        phoneNumber: group.primaryPhone,
        executedBy: executedBy,
        executedAt: new Date().toISOString(),
        newValue: primaryExpireDate,
        reason: `수동 만료일 동기화 - 주 계정 기준`,
      };

      await batchUpdate(updates);
      console.log(`그룹 ${group.groupId}의 만료일이 수동으로 동기화되었습니다.`);
    }
  }

  /**
   * 이력 ID 생성
   */
  private generateHistoryId(): string {
    return `HST_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

export const expireDateSyncService = new ExpireDateSyncService();